import { useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { useSnackbar } from 'notistack';
import { useForm, Resolver } from 'react-hook-form';

import { ControlledAutocompleteField } from '../../../../../../components/FormControlsV2/ControlledAutocompleteField';
import { ControlledDateTimeField } from '../../../../../../components/FormControlsV2/ControlledDateTimeField';
import { ControlledSelectField } from '../../../../../../components/FormControlsV2/ControlledSelectField';
import { ControlledTextField } from '../../../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../../../components/FormControlsV2/FormProvider';
import { StrategiesForList$ } from '../../../../../../effector/strategies';
import {
  Promotion,
  PromotionFormData,
  PromotionRequest,
  ServiceType,
  ClientCondition,
  serviceTypeLabels,
  clientConditionLabels,
  conditionsWithExtraDays,
} from '../../../../../../types/promotion';
import { Tariff } from '../../../../../../types/tariffInfo';
import { parseApiError, isErrorResponse } from '../../../../../../utils/apiErrors';
import { useCreatePromotion, useUpdatePromotion } from '../../../hooks';
import { schema } from '../schema';

const serviceTypeOptions = Object.entries(serviceTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

const clientConditionOptions = Object.entries(clientConditionLabels).map(([value, label]) => ({
  value,
  label,
}));

interface Props {
  promotion?: Promotion;
  tariffs: Tariff[];
  onUpdate: () => void;
}

export function PromotionForm({ promotion, tariffs, onUpdate }: Props) {
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const strategiesStore = useUnit(StrategiesForList$);

  const { mutateAsync: createAsync, isPending: isCreating } = useCreatePromotion();
  const { mutateAsync: updateAsync, isPending: isUpdating } = useUpdatePromotion();

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!promotion;
  const isActiveCampaign = !!promotion?.active;
  const hasStarted = isActiveCampaign && dayjs(promotion.startDate).isBefore(dayjs());

  const strategyOptions = useMemo(
    () => strategiesStore.map((s) => ({ value: s.id, label: s.name })),
    [strategiesStore],
  );

  const defaultValues: PromotionFormData = {
    name: promotion?.name ?? '',
    serviceType: promotion?.serviceType != null ? String(promotion.serviceType) : '',
    clientConditions: promotion?.clientConditions != null ? String(promotion.clientConditions) : '',
    clientConditionsDays: promotion?.clientConditionsDays ?? null,
    strategyIds: promotion?.strategyIds ?? [],
    tariffId: promotion?.tariffId?.toLowerCase() ?? '',
    tariffDisplayValue: promotion?.tariffDisplayValue ?? '',
    tariffDescription: promotion?.tariffDescription ?? '',
    tariffInfo: promotion?.tariffInfo ?? '',
    startDate: promotion?.startDate ?? null,
    endDate: promotion?.endDate ?? null,
    returnDate: promotion?.returnDate ?? null,
    limitTime: promotion?.limitTime ?? null,
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<PromotionFormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema) as Resolver<PromotionFormData>,
  });

  const clientConditions = watch('clientConditions');
  const selectedServiceType = watch('serviceType');

  const showExtraDays = conditionsWithExtraDays.includes(Number(clientConditions) as ClientCondition);

  const tariffOptions = useMemo(
    () =>
      tariffs
        .filter((t) => !selectedServiceType || t.kind === Number(selectedServiceType))
        .map((t) => ({ value: t.guid.toLowerCase(), label: `${t.description} (${t.category})` })),
    [tariffs, selectedServiceType],
  );

  const prevServiceTypeRef = useRef(defaultValues.serviceType);
  useEffect(() => {
    if (prevServiceTypeRef.current && selectedServiceType !== prevServiceTypeRef.current) {
      setValue('tariffId', '', { shouldDirty: true });
    }
    prevServiceTypeRef.current = selectedServiceType;
  }, [selectedServiceType, setValue]);

  const onSubmit = async (values: PromotionFormData) => {
    setError('');

    const request: PromotionRequest = {
      name: values.name,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(values.endDate).format('YYYY-MM-DD'),
      returnDate: values.returnDate ? dayjs(values.returnDate).format('YYYY-MM-DD') : null,
      limitTime: values.limitTime || null,
      clientConditionsDays: showExtraDays ? values.clientConditionsDays : null,
      serviceType: Number(values.serviceType) as ServiceType,
      clientConditions: Number(values.clientConditions) as ClientCondition,
      strategyIds: values.strategyIds,
      tariffId: values.tariffId,
      tariffDisplayValue: values.tariffDisplayValue ?? '',
      tariffDescription: values.tariffDescription ?? '',
      tariffInfo: values.tariffInfo ?? '',
    };

    try {
      let result;

      if (isEditMode) {
        result = await updateAsync({ id: promotion!.id, data: { ...request, id: promotion!.id } });
      } else {
        result = await createAsync(request);
      }

      if (isErrorResponse(result)) {
        setError(parseApiError(result));

        return;
      }

      onUpdate();
      enqueueSnackbar(isEditMode ? 'Акция обновлена' : 'Акция создана');
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const isFieldDisabled = (fieldName: string) => {
    if (!isActiveCampaign || !hasStarted) return false;

    const alwaysEditableAfterStart = new Set(['endDate', 'tariffDisplayValue', 'tariffDescription', 'tariffInfo']);

    return !alwaysEditableAfterStart.has(fieldName);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
      <FormProvider control={control} errors={errors}>
        {isActiveCampaign && hasStarted && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            Кампания активна и уже началась. Можно менять только дату завершения и описания акционных тарифов.
          </Typography>
        )}

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Описание
        </Typography>
        <ControlledTextField name="name" label="Название" disabled={isFieldDisabled('name')} />
        <ControlledSelectField
          name="serviceType"
          label="Услуга"
          options={serviceTypeOptions}
          disabled={isFieldDisabled('serviceType')}
        />
        <ControlledSelectField
          name="clientConditions"
          label="Участники"
          options={clientConditionOptions}
          disabled={isFieldDisabled('clientConditions')}
        />
        {showExtraDays && (
          <ControlledTextField
            name="clientConditionsDays"
            label="Доп. кол-во дней для условий по клиентам"
            type="number"
            disabled={isFieldDisabled('clientConditionsDays')}
          />
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Стратегии
          </Typography>
          <ControlledAutocompleteField
            name="strategyIds"
            label="Стратегии"
            options={strategyOptions}
            multiple
            disabled={isFieldDisabled('strategyIds')}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Тариф
          </Typography>
          <ControlledSelectField
            name="tariffId"
            label="Тариф"
            options={tariffOptions}
            disabled={isFieldDisabled('tariffId')}
          />
          <ControlledTextField
            name="tariffDisplayValue"
            label="Отображаемое значение тарифа"
            disabled={isFieldDisabled('tariffDisplayValue')}
          />
          <ControlledTextField
            name="tariffDescription"
            label="Описание тарифа"
            disabled={isFieldDisabled('tariffDescription')}
          />
          <ControlledTextField
            name="tariffInfo"
            label="Доп. информация по тарифу"
            disabled={isFieldDisabled('tariffInfo')}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Срок действия акции
          </Typography>
          <Stack direction="row" gap={2}>
            <ControlledDateTimeField
              name="startDate"
              label="Начало"
              format="DD.MM.YYYY"
              disabled={isFieldDisabled('startDate')}
            />
            <ControlledDateTimeField name="endDate" label="Завершение" format="DD.MM.YYYY" />
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Дополнительно (опционально)
          </Typography>
          <ControlledDateTimeField
            name="returnDate"
            label="Дата смены тарифа назад"
            format="DD.MM.YYYY"
            disabled={isFieldDisabled('returnDate')}
          />
          <ControlledTextField
            name="limitTime"
            label="Срок нахождения на акции (дней)"
            type="number"
            disabled={isFieldDisabled('limitTime')}
          />
          <Typography variant="caption" color="text.secondary">
            Может быть задано только одно из: дата смены тарифа или срок нахождения на акции
          </Typography>
        </Box>
      </FormProvider>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!isDirty || isLoading}
        fullWidth
        sx={{ mt: 2 }}
        startIcon={isLoading && <CircularProgress size={20} />}
      >
        {isEditMode ? 'Сохранить' : 'Добавить'}
      </Button>
    </form>
  );
}
